import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router";
import { Button, EmptyState, Icon, Input, PageBody, Pill, Select, Sheet, SkeletonText, Textarea, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { GOAL_STATUS_OPTIONS, goalStatusLabel, goalStatusTone, metricLabel, progressLabel } from "../lib/goals.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./goals.css";

/* Mirrors packages/api/src/routes/goals.ts. */
export interface Goal {
  id: string;
  parentId: string | null;
  title: string;
  description: string | null;
  ownerId: string;
  ownerName: string;
  teamId: string | null;
  teamName: string | null;
  periodLabel: string;
  startDate: string;
  endDate: string;
  measurement: "projects" | "manual";
  unit: string | null;
  startValue: number | null;
  targetValue: number | null;
  currentValue: number | null;
  statusOverride: string | null;
  progress: number | null;
  suggestedStatus: string;
  status: string;
  projectCount: number;
  canEdit: boolean;
}
type Objective = Goal & { keyResults: Goal[] };
type LinkedProject = { id: string; code: string; name: string; archived: boolean; progress: number | null };
type CheckIn = {
  id: string;
  authorName: string;
  value: number | null;
  status: string | null;
  progress: number | null;
  note: string | null;
  createdAt: string;
};
type Detail = {
  goal: Goal;
  parent: { id: string; title: string } | null;
  keyResults: Goal[];
  links: LinkedProject[];
  hiddenLinkCount: number;
  checkIns: CheckIn[];
  checkInTotal: number;
};
type Person = { id: string; displayName: string };
type Team = { id: string; name: string };
type ProjectOption = { id: string; code: string; name: string };

const PAGE = 25;

export function ProgressBar({ value, label }: { value: number | null; label: string }) {
  return (
    <div className="goal-bar" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={value ?? undefined}>
      <span style={{ width: `${value ?? 0}%` }} />
    </div>
  );
}

function GoalRow({ goal, depth, onOpen, toggle }: { goal: Goal; depth: number; onOpen: (id: string) => void; toggle?: ReactToggle }) {
  return (
    <div className="goal-row" style={{ paddingLeft: depth * 24 }}>
      {toggle ? (
        <button
          type="button"
          className="goal-row__toggle"
          aria-label={`${toggle.open ? "Collapse" : "Expand"} ${goal.title}`}
          onClick={toggle.onToggle}
        >
          <Icon name={toggle.open ? "chevron-down" : "chevron-right"} size={14} />
        </button>
      ) : (
        <span className="goal-row__toggle" aria-hidden="true" />
      )}
      <button type="button" className="goal-row__title" onClick={() => onOpen(goal.id)}>
        {goal.title}
      </button>
      <span className="goal-row__owner">{goal.ownerName}</span>
      <ProgressBar value={goal.progress} label={`${goal.title} progress`} />
      <span className="goal-row__pct">{progressLabel(goal.progress)}</span>
      <Pill tone={goalStatusTone(goal.status)} dot>
        {goalStatusLabel(goal.status)}
      </Pill>
    </div>
  );
}
type ReactToggle = { open: boolean; onToggle: () => void };

export function Goals() {
  usePageTitle("Goals");
  const { toast } = useToast();
  const [period, setPeriod] = useState<string | null>(null);
  const [periods, setPeriods] = useState<string[]>([]);
  const [rows, setRows] = useState<Objective[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(
    async (offset: number) => {
      const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
      if (period) params.set("period", period);
      try {
        const res = await api.get<{ goals: Objective[]; periods: string[]; canCreate: boolean; total: number; hasMore: boolean }>(
          `/goals?${params.toString()}`,
        );
        setRows((prev) => (offset === 0 ? res.goals : [...prev, ...res.goals]));
        setPeriods(res.periods);
        setTotal(res.total);
        setHasMore(res.hasMore);
        setCanCreate(res.canCreate);
        setFailed(false);
        // First load: show the newest period rather than everything.
        if (period === null && res.periods[0]) setPeriod(res.periods[0]);
      } catch (error) {
        setFailed(true);
        toast(error instanceof ApiError ? error.message : "Could not load goals.", { tone: "danger" });
      } finally {
        setLoading(false);
      }
    },
    [period, toast],
  );

  useEffect(() => {
    setLoading(true);
    void load(0);
  }, [load]);

  const refresh = () => void load(0);

  return (
    <PageBody
      title="Goals"
      subtitle="Objectives and key results, rolled up from the projects that deliver them."
      width="regular"
      actions={
        canCreate && (
          <Button size="sm" icon={<Icon name="plus" size={13} />} onClick={() => setCreating(true)}>
            New goal
          </Button>
        )
      }
    >
      {periods.length > 0 && (
        <div className="goals-filters">
          <Select label="Period" value={period ?? ""} onChange={(e) => setPeriod(e.target.value)}>
            {periods.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
        </div>
      )}

      {loading ? (
        <SkeletonText lines={4} />
      ) : failed && rows.length === 0 ? (
        <EmptyState icon="insights" title="Goals could not be loaded" description="Check your connection and try again." />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="insights"
          title="No goals yet"
          description={canCreate ? "Create an objective and link the projects that deliver it." : "Managers and admins create goals for the office."}
        />
      ) : (
        <>
          <div className="goal-tree" role="tree" aria-label="Goals">
            {rows.map((o: Objective) => {
              const open = !collapsed.has(o.id);
              return (
                <div key={o.id} role="treeitem" aria-expanded={o.keyResults.length ? open : undefined} aria-selected={false} className="goal-group">
                  <GoalRow
                    goal={o}
                    depth={0}
                    onOpen={setOpenId}
                    toggle={
                      o.keyResults.length
                        ? {
                            open,
                            onToggle: () =>
                              setCollapsed((prev) => {
                                const next = new Set(prev);
                                if (next.has(o.id)) next.delete(o.id);
                                else next.add(o.id);
                                return next;
                              }),
                          }
                        : undefined
                    }
                  />
                  {open && o.keyResults.map((k) => <GoalRow key={k.id} goal={k} depth={1} onOpen={setOpenId} />)}
                </div>
              );
            })}
          </div>
          {hasMore && (
            <Button variant="secondary" size="sm" onClick={() => void load(rows.length)}>
              Show more ({total - rows.length} left)
            </Button>
          )}
        </>
      )}

      <GoalSheet goalId={openId} onClose={() => setOpenId(null)} onChanged={refresh} onOpen={setOpenId} />
      <CreateGoalSheet
        open={creating}
        period={period}
        objectives={rows}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          refresh();
        }}
      />
    </PageBody>
  );
}

/* ───────────────────────────── detail sheet ───────────────────────────── */

function GoalSheet({
  goalId,
  onClose,
  onChanged,
  onOpen,
}: {
  goalId: string | null;
  onClose: () => void;
  onChanged: () => void;
  onOpen: (id: string) => void;
}) {
  const { toast } = useToast();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [linkId, setLinkId] = useState("");
  const [value, setValue] = useState("");
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!goalId) return;
    try {
      setDetail(await api.get<Detail>(`/goals/${goalId}`));
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not load the goal.", { tone: "danger" });
    }
  }, [goalId, toast]);

  useEffect(() => {
    setDetail(null);
    setValue("");
    setStatus("");
    setNote("");
    void load();
  }, [load]);

  const canEdit = detail?.goal.canEdit ?? false;
  useEffect(() => {
    if (!canEdit) return;
    void api
      .get<{ projects: ProjectOption[] }>("/projects")
      .then((r) => setProjects(r.projects))
      .catch(() => setProjects([]));
  }, [canEdit, goalId]);

  const run = async (action: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    try {
      await action();
      await load();
      onChanged();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : failure, { tone: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const goal = detail?.goal;
  const linkedIds = new Set(detail?.links.map((l) => l.id));

  const submitCheckIn = (e: FormEvent) => {
    e.preventDefault();
    if (!goal) return;
    const body: Record<string, unknown> = {};
    if (value.trim() !== "") body.value = Number(value);
    if (status) body.status = status;
    if (note.trim()) body.note = note.trim();
    void run(async () => {
      await api.post(`/goals/${goal.id}/check-ins`, body);
      setValue("");
      setStatus("");
      setNote("");
    }, "Could not save the check-in.");
  };

  return (
    <Sheet open={goalId !== null} onClose={onClose} title={goal?.title ?? "Goal"}>
      {!detail || !goal ? (
        <SkeletonText lines={5} />
      ) : (
        <div className="goal-detail">
          {detail.parent && (
            <p className="goal-detail__crumb">
              Key result of{" "}
              <button type="button" className="goal-link" onClick={() => onOpen(detail.parent!.id)}>
                {detail.parent.title}
              </button>
            </p>
          )}
          <div className="goal-detail__head">
            <Pill tone={goalStatusTone(goal.status)} dot>
              {goalStatusLabel(goal.status)}
            </Pill>
            <span className="goal-detail__pct">{progressLabel(goal.progress)}</span>
            <ProgressBar value={goal.progress} label="Progress" />
          </div>
          <p className="goal-detail__meta">
            {goal.ownerName} · {goal.periodLabel} ({goal.startDate} to {goal.endDate})
            {goal.teamName ? ` · ${goal.teamName}` : ""}
            {metricLabel(goal) ? ` · ${metricLabel(goal)}` : ""}
          </p>
          {goal.description && <p>{goal.description}</p>}
          {goal.statusOverride && (
            <p className="goal-detail__meta">
              Status set by the owner; the numbers suggest {goalStatusLabel(goal.suggestedStatus).toLowerCase()}.
              {canEdit && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="goal-link"
                    disabled={busy}
                    onClick={() => void run(() => api.patch(`/goals/${goal.id}`, { statusOverride: null }), "Could not update the status.")}
                  >
                    Use the suggestion
                  </button>
                </>
              )}
            </p>
          )}

          {detail.keyResults.length > 0 && (
            <section>
              <h3>Key results</h3>
              {detail.keyResults.map((k) => (
                <GoalRow key={k.id} goal={k} depth={0} onOpen={onOpen} />
              ))}
            </section>
          )}

          {goal.measurement === "projects" && (
            <section>
              <h3>Linked projects</h3>
              {detail.links.length === 0 && detail.hiddenLinkCount === 0 && <p className="goal-detail__meta">No projects linked yet.</p>}
              <ul className="goal-links">
                {detail.links.map((p) => (
                  <li key={p.id}>
                    <Link to={`/work/project/${p.id}`}>
                      {p.code} {p.name}
                    </Link>
                    <span className="goal-detail__meta">{p.archived ? "Archived, not counted" : progressLabel(p.progress)}</span>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        aria-label={`Unlink ${p.name}`}
                        onClick={() => void run(() => api.delete(`/goals/${goal.id}/links/${p.id}`), "Could not unlink the project.")}
                      >
                        Unlink
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
              {detail.hiddenLinkCount > 0 && (
                <p className="goal-detail__meta">
                  {detail.hiddenLinkCount} more linked {detail.hiddenLinkCount === 1 ? "project is" : "projects are"} not visible to you.
                </p>
              )}
              {canEdit && (
                <div className="goal-inline">
                  <Select label="Link a project" value={linkId} onChange={(e) => setLinkId(e.target.value)}>
                    <option value="">Choose a project</option>
                    {projects
                      .filter((p) => !linkedIds.has(p.id))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code} {p.name}
                        </option>
                      ))}
                  </Select>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!linkId || busy}
                    onClick={() =>
                      void run(async () => {
                        await api.post(`/goals/${goal.id}/links`, { projectId: linkId });
                        setLinkId("");
                      }, "Could not link the project.")
                    }
                  >
                    Link
                  </Button>
                </div>
              )}
            </section>
          )}

          <section>
            <h3>Check-ins</h3>
            {canEdit && (
              <form className="goal-form" onSubmit={submitCheckIn}>
                {goal.measurement === "manual" && (
                  <Input
                    label={`Current value${goal.unit ? ` (${goal.unit})` : ""}`}
                    type="number"
                    step="any"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                )}
                <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">Keep as is</option>
                  {GOAL_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
                <Button type="submit" size="sm" disabled={busy || (value.trim() === "" && !status && !note.trim())}>
                  Add check-in
                </Button>
              </form>
            )}
            {detail.checkIns.length === 0 ? (
              <p className="goal-detail__meta">No check-ins yet.</p>
            ) : (
              <ul className="goal-checkins">
                {detail.checkIns.map((c) => (
                  <li key={c.id}>
                    <div className="goal-checkins__head">
                      <strong>{c.authorName}</strong>
                      <time dateTime={c.createdAt}>{new Date(c.createdAt).toLocaleDateString()}</time>
                      {c.progress !== null && <span>{c.progress}%</span>}
                      {c.value !== null && <span>{c.value}</span>}
                      {c.status && <Pill tone={goalStatusTone(c.status)}>{goalStatusLabel(c.status)}</Pill>}
                    </div>
                    {c.note && <p>{c.note}</p>}
                  </li>
                ))}
              </ul>
            )}
            {detail.checkInTotal > detail.checkIns.length && (
              <p className="goal-detail__meta">Showing the latest {detail.checkIns.length} of {detail.checkInTotal}.</p>
            )}
          </section>

          {canEdit && (
            <Button
              variant="danger"
              size="sm"
              disabled={busy}
              onClick={() => {
                if (!window.confirm(`Delete "${goal.title}" and its key results?`)) return;
                void (async () => {
                  setBusy(true);
                  try {
                    await api.delete(`/goals/${goal.id}`);
                    onClose();
                    onChanged();
                  } catch (error) {
                    toast(error instanceof ApiError ? error.message : "Could not delete the goal.", { tone: "danger" });
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            >
              Delete goal
            </Button>
          )}
        </div>
      )}
    </Sheet>
  );
}

/* ───────────────────────────── create sheet ───────────────────────────── */

function CreateGoalSheet({
  open,
  period,
  objectives,
  onClose,
  onCreated,
}: {
  open: boolean;
  period: string | null;
  objectives: Objective[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const [people, setPeople] = useState<Person[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [parentId, setParentId] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [measurement, setMeasurement] = useState<"projects" | "manual">("projects");
  const [unit, setUnit] = useState("");
  const [start, setStart] = useState("0");
  const [target, setTarget] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    void api.get<{ people: Person[] }>("/people").then((r) => setPeople(r.people)).catch(() => setPeople([]));
    void api.get<{ teams: Team[] }>("/teams?limit=200").then((r) => setTeams(r.teams)).catch(() => setTeams([]));
    setPeriodLabel(period ?? "");
  }, [open, period]);

  const parent = objectives.find((o) => o.id === parentId);
  const needsPeriod = !parent;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/goals", {
        title,
        description: description.trim() || null,
        ...(ownerId ? { ownerId } : {}),
        teamId: teamId || null,
        ...(parentId ? { parentId } : { periodLabel, startDate, endDate }),
        measurement,
        ...(measurement === "manual"
          ? { unit: unit.trim() || null, startValue: Number(start), targetValue: Number(target), currentValue: Number(start) }
          : {}),
      });
      setTitle("");
      setDescription("");
      onCreated();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not create the goal.", { tone: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const valid =
    title.trim() !== "" &&
    (!needsPeriod || (periodLabel.trim() !== "" && startDate !== "" && endDate !== "")) &&
    (measurement === "projects" || (start !== "" && target !== ""));

  return (
    <Sheet open={open} onClose={onClose} title="New goal">
      <form className="goal-form" onSubmit={(e) => void submit(e)}>
        <Select label="Level" value={parentId} onChange={(e) => setParentId(e.target.value)}>
          <option value="">Objective</option>
          {objectives.map((o) => (
            <option key={o.id} value={o.id}>
              Key result of: {o.title}
            </option>
          ))}
        </Select>
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} />
        <Textarea label="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        <Select label="Owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
          <option value="">Me</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.displayName}
            </option>
          ))}
        </Select>
        <Select label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">No team</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        {needsPeriod && (
          <>
            <Input label="Period" hint="For example 2026 Q4" value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} required />
            <Input label="Starts" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            <Input label="Ends" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
          </>
        )}
        <Select label="Measured by" value={measurement} onChange={(e) => setMeasurement(e.target.value as "projects" | "manual")}>
          <option value="projects">Linked projects</option>
          <option value="manual">A number I update</option>
        </Select>
        {measurement === "manual" && (
          <>
            <Input label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} maxLength={24} />
            <Input label="Start value" type="number" step="any" value={start} onChange={(e) => setStart(e.target.value)} required />
            <Input label="Target value" type="number" step="any" value={target} onChange={(e) => setTarget(e.target.value)} required />
          </>
        )}
        <Button type="submit" loading={saving} disabled={!valid}>
          Create goal
        </Button>
      </form>
    </Sheet>
  );
}
